import {
  adminApiClient,
  extractDownloadFilename,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import type {
  ApiResponse,
  PaginatedResponse,
  AdminUser,
  AdminRole,
  DashboardMetricsResponse,
  AdminUserOrderDefaultsRefreshResult,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'
import { toBooleanValue } from '@/services/admin/valueHelpers'

export interface UserListParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  role?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface UserLoginHistoryParams {
  page?: number
  limit?: number
  from?: string
  to?: string
  provider?: 'google' | 'github'
}

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'users'],
    preferExplicitKeys: true,
  })
  return buildPaginatedResponse(payload, items, options)
}

const mapAdminUserRecord = (record: any): AdminUser => ({
  id: record?.id ?? '',
  email: record?.email ?? '',
  username: record?.username ?? '',
  role_id: record?.role_id ?? record?.roleId ?? '',
  role_code: record?.role_code ?? record?.roleCode ?? undefined,
  is_active: toBooleanValue(record?.is_active ?? record?.isActive, false),
  is_mfa_enabled: toBooleanValue(record?.is_mfa_enabled ?? record?.isMfaEnabled, false),
  is_email_verified: toBooleanValue(record?.is_email_verified ?? record?.isEmailVerified, false),
  last_login_at: record?.last_login_at ?? record?.lastLoginAt ?? undefined,
  last_login_ip: record?.last_login_ip ?? record?.lastLoginIp ?? undefined,
  login_attempts: Number(record?.login_attempts ?? record?.loginAttempts ?? 0),
  locked_until: record?.locked_until ?? record?.lockedUntil ?? undefined,
  created_by: record?.created_by ?? record?.createdBy ?? undefined,
  updated_by: record?.updated_by ?? record?.updatedBy ?? undefined,
  created_at: record?.created_at ?? record?.createdAt ?? new Date().toISOString(),
  updated_at: record?.updated_at ?? record?.updatedAt ?? new Date().toISOString(),
})

const mapAdminRoleRecord = (record: any): AdminRole => ({
  id: record?.id ?? '',
  code: record?.code ?? '',
  name: record?.name ?? '',
  description: record?.description ?? undefined,
  level: record?.level != null ? Number(record.level) : 0,
  is_active: toBooleanValue(record?.is_active ?? record?.isActive, true),
  created_at: record?.created_at ?? record?.createdAt ?? '',
})

const mapPlatformUserRecord = (record: any): any => ({
  ...record,
  userBusinessId: record?.userBusinessId ?? record?.user_business_id ?? undefined,
  user_business_id: record?.user_business_id ?? record?.userBusinessId ?? undefined,
  status:
    record?.status ??
    (toBooleanValue(record?.is_active ?? record?.isActive) ? 'active' : 'inactive'),
  is_active: toBooleanValue(record?.is_active ?? record?.isActive, false),
  created_at: record?.created_at ?? record?.createdAt ?? new Date().toISOString(),
  updated_at: record?.updated_at ?? record?.updatedAt ?? new Date().toISOString(),
  last_login_at: record?.last_login_at ?? record?.lastLoginAt ?? undefined,
  last_login_ip: record?.last_login_ip ?? record?.lastLoginIp ?? undefined,
  username: record?.username ?? record?.user_name ?? undefined,
  is_mfa_enabled: toBooleanValue(record?.is_mfa_enabled ?? record?.isMfaEnabled, false),
  is_email_verified: toBooleanValue(record?.is_email_verified ?? record?.isEmailVerified, false),
  login_attempts: record?.login_attempts ?? record?.loginAttempts ?? undefined,
  locked_until: record?.locked_until ?? record?.lockedUntil ?? null,
  currentSubscription: record?.currentSubscription ?? record?.current_subscription ?? undefined,
  currentPlan: record?.currentPlan ?? record?.current_plan ?? undefined,
})

export const userService = {
  async getDashboardMetrics(mode?: NetworkEnvironment): Promise<DashboardMetricsResponse> {
    try {
      const response = await adminApiClient.get<ApiResponse<DashboardMetricsResponse>>(
        '/admin/dashboard/metrics',
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Dashboard payload missing')
      }
      return response.data.data
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error)
      throw error
    }
  },

  async getAllAdminUsers(
    params?: UserListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<AdminUser>> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.search) searchParams.append('search', params.search)
    if (params?.role) searchParams.append('role', params.role)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    const url = query ? `/admin/users?${query}` : '/admin/users'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<AdminUser | any>(response.data, {
      keys: ['users'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
    const items = normalized.data ?? []
    const mapped = items.map(mapAdminUserRecord)
    return {
      ...normalized,
      data: mapped,
    }
  },

  async getRoles(mode?: NetworkEnvironment): Promise<ApiResponse<AdminRole[]>> {
    const response = await adminApiClient.get<ApiResponse<AdminRole[]>>(
      '/admin/roles',
      buildModeAwareConfig(mode)
    )
    const payload = response.data
    const rawRoles = (payload as any)?.data?.roles ?? (payload as any)?.data ?? []
    const roles = Array.isArray(rawRoles) ? rawRoles.map(mapAdminRoleRecord) : []
    return {
      ...payload,
      data: roles,
    }
  },

  async createAdminUser(data: {
    email: string
    username: string
    password: string
    role_id: string
  }): Promise<ApiResponse<AdminUser>> {
    const response = await adminApiClient.post<ApiResponse<any>>('/admin/users', data)
    const payload = response.data
    if (payload?.data) {
      return {
        ...payload,
        data: mapAdminUserRecord(payload.data),
      }
    }
    return payload
  },

  async getAdminUserById(
    userId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<AdminUser>> {
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/users/${userId}`,
      buildModeAwareConfig(mode)
    )
    const payload = response.data
    if (payload?.data) {
      return {
        ...payload,
        data: mapAdminUserRecord(payload.data),
      }
    }
    return payload
  },

  async updateAdminUser(userId: string, data: {
    email?: string
    username?: string
    role_id?: string
  }): Promise<ApiResponse<AdminUser>> {
    const response = await adminApiClient.put<ApiResponse<any>>(`/admin/users/${userId}`, data)
    const payload = response.data
    if (payload?.data) {
      return {
        ...payload,
        data: mapAdminUserRecord(payload.data),
      }
    }
    return payload
  },

  async changeAdminUserPassword(userId: string, data: {
    new_password: string
    current_password?: string
  }): Promise<ApiResponse<void>> {
    const response = await adminApiClient.put<ApiResponse<void>>(`/admin/users/${userId}/password`, data)
    return response.data
  },

  async updateAdminUserStatus(userId: string, data: {
    is_active: boolean
    reason?: string
  }): Promise<ApiResponse<AdminUser>> {
    const response = await adminApiClient.put<ApiResponse<any>>(`/admin/users/${userId}/status`, data)
    const payload = response.data
    if (payload?.data) {
      return {
        ...payload,
        data: mapAdminUserRecord(payload.data),
      }
    }
    return payload
  },

  async getAllUsers(
    params?: UserListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<any>> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    else if (params?.role) searchParams.append('status', params.role)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    const url = query ? `/admin/platform-users?${query}` : '/admin/platform-users'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<any>(response.data, {
      keys: ['users'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
    const mapped = (normalized.data ?? []).map(mapPlatformUserRecord)
    return {
      ...normalized,
      data: mapped,
    }
  },

  async exportCsv(
    params?: UserListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/platform-users/export', {
        params: {
          search: params?.search,
          status: params?.status ?? params?.role,
          sortBy: params?.sortBy,
          sortOrder: params?.sortOrder,
        },
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `platform-users-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export platform users'))
    }
  },

  async getUserById(userId: string, mode?: NetworkEnvironment): Promise<any> {
    try {
      const response = await adminApiClient.get<ApiResponse<any>>(
        `/admin/platform-users/${userId}`,
        buildModeAwareConfig(mode)
      )
      const user = response?.data?.data?.user ?? response?.data?.data
      if (user) {
        return mapPlatformUserRecord(user)
      }

      throw new Error(`User ${userId} API returned no data`)
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error)
      const apiMessage =
        (error as any)?.response?.data?.error?.message ??
        (error as any)?.response?.data?.message ??
        (error instanceof Error ? error.message : null)
      throw new Error(apiMessage || `Failed to fetch user ${userId}`)
    }
  },

  async getUserLoginHistory(
    userId: string,
    params?: UserLoginHistoryParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<any>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', String(params.page))
    if (params?.limit) searchParams.append('limit', String(params.limit))
    if (params?.from) searchParams.append('from', params.from)
    if (params?.to) searchParams.append('to', params.to)
    if (params?.provider) searchParams.append('provider', params.provider)

    const query = searchParams.toString()
    const url = `/admin/platform-users/${userId}/login-history${query ? `?${query}` : ''}`
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const payload = response.data
    const items = Array.isArray(payload?.data?.items) ? payload.data.items : []
    const pagination = payload?.data?.pagination
    return {
      success: payload?.success !== false,
      data: items,
      pagination: {
        page: Number(pagination?.page ?? params?.page ?? 1),
        limit: Number(pagination?.limit ?? params?.limit ?? 20),
        total: Number(pagination?.total ?? items.length),
        pages: Number(pagination?.pages ?? pagination?.total_pages ?? pagination?.totalPages ?? 1),
        total_pages: Number(pagination?.total_pages ?? pagination?.totalPages ?? 1),
      },
    }
  },

  async updatePlatformUser(
    userId: string,
    data: { 
      email?: string; 
      username?: string; 
      is_active?: boolean; 
      is_email_verified?: boolean;
    }
  ): Promise<any> {
    const response = await adminApiClient.put<ApiResponse<any>>(`/admin/platform-users/${userId}`, data)
    const user = response?.data?.data?.user ?? response?.data?.data ?? response?.data
    return mapPlatformUserRecord(user)
  },

  async resetPlatformUserMfa(userId: string, reason: string): Promise<{
    userId: string
    mfaEnabled: false
    sessionsRevoked: true
  }> {
    const response = await adminApiClient.post<
      ApiResponse<{
        userId: string
        mfaEnabled: false
        sessionsRevoked: true
      }>
    >(`/admin/platform-users/${userId}/mfa/reset`, { reason })
    return response.data.data!
  },

  async updatePlatformUserStatus(userId: string, data: { is_active: boolean; reason?: string }): Promise<any> {
    const response = await adminApiClient.put<ApiResponse<any>>(`/admin/platform-users/${userId}/status`, data)
    const user = response?.data?.data?.user ?? response?.data?.data ?? response?.data
    return mapPlatformUserRecord(user)
  },

  async changePlatformUserPlan(
    userId: string,
    data: { planId: string; endsAt?: string | null; reason: string },
    mode?: NetworkEnvironment
  ): Promise<{
    subscription: any
    snapshotRebuild: { merchantCount: number; rebuiltCount: number; failedMerchantIds: string[] }
  }> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/platform-users/${userId}/subscription/change-plan`,
      data,
      buildModeAwareConfig(mode)
    )
    return response?.data?.data
  },

  async refreshUserMerchantOrderDefaults(
    userId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminUserOrderDefaultsRefreshResult> {
    const response = await adminApiClient.post<ApiResponse<AdminUserOrderDefaultsRefreshResult>>(
      `/admin/users/${userId}/order-defaults/refresh`,
      {},
      buildModeAwareConfig(mode)
    )
    if (!response.data?.data) {
      throw new Error(`User ${userId} refresh API returned no data`)
    }
    return response.data.data
  },

  async recomputeUserMerchantSnapshots(
    userId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminUserOrderDefaultsRefreshResult> {
    const response = await adminApiClient.post<ApiResponse<AdminUserOrderDefaultsRefreshResult>>(
      `/admin/users/${userId}/snapshot/recompute`,
      {},
      buildModeAwareConfig(mode)
    )
    if (!response.data?.data) {
      throw new Error(`User ${userId} snapshot recompute API returned no data`)
    }
    return response.data.data
  },
}
